import { inject, Injectable, signal } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

import { API_ROUTES } from '../consts/api-routes.const';
import { DealerConfig } from '../interfaces';
import { dealerConfig } from '../state/dealer.config';
import { isValidDealerId } from './dealer-id';
import { HttpService } from './http.service';
import { ThemeService } from './theme.service';

@Injectable({ providedIn: 'root' })
export class DealerService {
  private readonly httpService: HttpService = inject(HttpService);
  private readonly themeService: ThemeService = inject(ThemeService);
  private readonly cache = new Map<string, DealerConfig>();

  public readonly currentDealerId = signal<string | null>(null);

  public setCurrentDealerId(rawValue: string | null | undefined): void {
    if (!rawValue) {
      this.currentDealerId.set(null);
      return;
    }
    const normalized: string = rawValue.trim();
    this.currentDealerId.set(isValidDealerId(normalized) ? normalized : null);
  }

  public getDealerConfig(dealerId: string): Observable<DealerConfig> {
    const cached = this.cache.get(dealerId);
    if (cached) {
      return of(cached);
    }

    return this.httpService
      .get<DealerConfig>(API_ROUTES.DEALER.config(dealerId))
      .pipe(
        tap(
          (dealerConfigResponse: DealerConfig): Map<string, DealerConfig> =>
            this.cache.set(dealerId, dealerConfigResponse),
        ),
      )
      .pipe(
        tap((dealerConfigResponse: DealerConfig): void =>
          this.themeService.applyApiTheming(dealerConfigResponse),
        ),
      )
      .pipe(
        tap((dealerConfigResponse: DealerConfig): void => dealerConfig.set(dealerConfigResponse)),
      );
  }
}
